import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus, Check } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { IMAGERY } from "../lib/imagery";
import { accent } from "../lib/accent";

function SectorCard({ idx, title, stat, body, items, image, open, onToggle, t }) {
  return (
    <article
      data-testid={`sector-card-${idx}`}
      className={`group relative flex flex-col bg-obsidian-700 border border-white/8 transition-all duration-500 ${
        open ? "lg:col-span-2 ring-1 ring-champagne/40" : "hover:border-white/15"
      }`}
    >
      {/* Visual */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/10" />
        <div className="absolute top-5 left-5 flex items-center gap-3">
          <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-champagne font-medium">
            0{idx}
          </span>
          <span className="block w-8 h-px bg-champagne" />
        </div>
      </div>

      {/* Body */}
      <div className="p-7 lg:p-8 flex-1 flex flex-col gap-5">
        <h3 className="font-serif text-2xl text-white leading-tight">
          {title}
        </h3>

        <p className="font-sans text-[12px] uppercase tracking-[0.18em] text-champagne font-medium">
          {stat}
        </p>

        <p className="text-white/70 text-[15px] leading-relaxed">{body}</p>

        {/* Expandable */}
        <div
          className={`grid transition-all duration-500 ease-out ${
            open
              ? "grid-rows-[1fr] opacity-100 mt-2"
              : "grid-rows-[0fr] opacity-0 mt-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-white/10 pt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {items.map((it, i) => (
                <div
                  key={i}
                  data-testid={`sector-${idx}-item-${i + 1}`}
                  className="flex items-start gap-3 text-white/85 text-[14px]"
                >
                  <Check
                    size={14}
                    className="mt-1 text-champagne flex-shrink-0"
                  />
                  <span>{it}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onToggle}
          data-testid={`sector-toggle-${idx}`}
          aria-expanded={open}
          className="mt-auto inline-flex items-center gap-3 self-start font-sans text-[11px] uppercase tracking-[0.22em] text-champagne hover:text-white transition-colors duration-300 font-medium"
        >
          <span className="w-8 h-8 inline-flex items-center justify-center border border-champagne/40 group-hover:border-champagne transition-colors">
            {open ? <Minus size={14} /> : <Plus size={14} />}
          </span>
          {open ? t.sectors.collapse : t.sectors.expand}
        </button>
      </div>
    </article>
  );
}

export default function Sectors() {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(-1);

  const sectors = [
    {
      title: t.sectors.s1Title,
      stat: t.sectors.s1Stat,
      body: t.sectors.s1Body,
      items: t.sectors.s1Items,
      image: IMAGERY.sectorHealthcare,
    },
    {
      title: t.sectors.s2Title,
      stat: t.sectors.s2Stat,
      body: t.sectors.s2Body,
      items: t.sectors.s2Items,
      image: IMAGERY.sectorAgribusiness,
    },
    {
      title: t.sectors.s3Title,
      stat: t.sectors.s3Stat,
      body: t.sectors.s3Body,
      items: t.sectors.s3Items,
      image: IMAGERY.sectorOilGas,
    },
    {
      title: t.sectors.s4Title,
      stat: t.sectors.s4Stat,
      body: t.sectors.s4Body,
      items: t.sectors.s4Items,
      image: IMAGERY.sectorMining,
    },
    {
      title: t.sectors.s5Title,
      stat: t.sectors.s5Stat,
      body: t.sectors.s5Body,
      items: t.sectors.s5Items,
      image: IMAGERY.sectorCleanEnergy,
    },
    {
      title: t.sectors.s6Title,
      stat: t.sectors.s6Stat,
      body: t.sectors.s6Body,
      items: t.sectors.s6Items,
      image: IMAGERY.sectorCommodities,
    },
  ];

  return (
    <>
      {/* HERO */}
      <section
        data-testid="sectors-hero"
        className="relative -mt-20 pt-40 pb-24 md:pb-32 bg-obsidian text-white overflow-hidden"
      >
        <img
          src={IMAGERY.sectorsHero}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-x relative z-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-10 animate-fade-in-up">
            <p className="eyebrow-light">{t.sectors.eyebrow}</p>
            <h1 className="display-h1 mt-6 max-w-[28ch] text-white">
              {accent(t.sectors.title)}
            </h1>
            <div className="section-divider mt-10 mr-auto ml-0" />
            <p className="font-sans text-lg md:text-xl text-white/80 mt-8 max-w-3xl leading-relaxed font-light">
              {t.sectors.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* SECTOR GRID */}
      <section className="bg-obsidian">
        <div className="container-x py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 auto-rows-min">
            {sectors.map((s, i) => (
              <SectorCard
                key={i}
                idx={i + 1}
                title={s.title}
                stat={s.stat}
                body={s.body}
                items={s.items}
                image={s.image}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                t={t}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-obsidian-700 border-t border-white/5">
        <div className="container-x py-24 md:py-28 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="eyebrow">Closing Mandate</p>
            <h2 className="display-h2 mt-5 max-w-[20ch] text-white">
              {accent(t.sectors.ctaTitle)}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Link
              to="/contact"
              data-testid="sectors-cta"
              className="group gold-btn"
            >
              {t.sectors.ctaBtn}
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
